import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Sparkles, Image as ImageIcon, Loader2 } from "lucide-react";
import { ChatMessage } from "@shared/schema";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/hooks/use-i18n";
import { PresetSelector, type PresetId } from "./PresetSelector";
import { captureEvent } from "@/lib/posthog";

interface ChatInterfaceProps {
  messages: ChatMessage[];
  onSendMessage: (message: string, selectedPresets?: PresetId[]) => void;
  onUploadImage?: () => void;
  isLoading?: boolean;
  selectedPresets: PresetId[];
  onTogglePreset: (id: PresetId) => void;
}

export function ChatInterface({
  messages,
  onSendMessage,
  onUploadImage,
  isLoading,
  selectedPresets,
  onTogglePreset,
}: ChatInterfaceProps) {
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return;
    captureEvent("chat_message_sent", {
      message_length: input.trim().length,
      selected_presets: selectedPresets,
      preset_count: selectedPresets.length,
      is_first_message: messages.length === 0,
    });
    onSendMessage(input.trim(), selectedPresets.length > 0 ? selectedPresets : undefined);
    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <ScrollArea className="flex-1 min-h-0 p-3 sm:p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-4 sm:py-8">
            <div className="relative mb-4 sm:mb-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <svg
                  className="w-8 h-8 sm:w-10 sm:h-10 text-primary animate-float"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <div className="absolute -top-1 -right-1">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary animate-pulse" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">{t("awardsPageBuilder")}</h3>
            <p className="text-muted-foreground text-center text-xs sm:text-sm mb-4 sm:mb-6 max-w-sm px-2">
              {t("selectSectionsDescription")}
            </p>
            <PresetSelector
              selectedPresets={selectedPresets}
              onTogglePreset={onTogglePreset}
              disabled={isLoading}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                    data-testid={`message-${message.role}-${message.id}`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-60 mt-1">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="bg-muted rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {t("creatingAwardsPage")}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </ScrollArea>

      <div className="border-t p-2 sm:p-4 bg-background shrink-0">
        <div className="flex items-end gap-2">
          {onUploadImage && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onUploadImage}
              disabled={isLoading}
              className="shrink-0"
              data-testid="button-upload-image"
            >
              <ImageIcon className="h-5 w-5" />
            </Button>
          )}
          <div className="flex-1 relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("chatPlaceholder")}
              className="min-h-[44px] max-h-[150px] resize-none pr-12 rounded-2xl"
              disabled={isLoading}
              data-testid="input-chat-message"
            />
          </div>
          <Button
            size="icon"
            onClick={handleSubmit}
            disabled={!input.trim() || isLoading}
            className="shrink-0 rounded-full h-11 w-11"
            data-testid="button-send-message"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
